import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "hooks";
import { btnPrimaryCls } from "components/tw/classes";
import { fetchAllFileCategories } from "services/files-category.service";
import { fetchFilesTypes } from "services/files-types.service";
import {
  CatalogTable,
  ChangeLink,
  SettingsCard,
  StatusBanner,
} from "containers/settings/ui";
import {
  fetchFilesTemplates,
  setDestroy,
} from "./files-templates-container.slice";

type Template = {
  id: string;
  name: string;
  // Ids: the list endpoint does not send the category title or the document type label.
  category?: string | null;
  templateType?: string | null;
  signType?: string | null;
};

type Category = { id: string; title: string };
type DocumentType = { id: string; label: string };

type NamesById = Record<string, string>;

// The names arrive in a second request; the table waits for it so the Category and
// Document type columns do not flash dashes first.
type LookupStatus = "loading" | "loaded" | "failed";

const DASH = "—";

function nameOf(namesById: NamesById, id?: string | null) {
  return (id && namesById[id]) || DASH;
}

function FilesTemplatesContainer() {
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { collection, isLoading, isError } = useAppSelector(
    (state) => state.filesTemplatesReducer
  );
  const [categoryTitles, setCategoryTitles] = useState<NamesById>({});
  const [documentTypeLabels, setDocumentTypeLabels] = useState<NamesById>({});
  const [lookupStatus, setLookupStatus] = useState<LookupStatus>("loading");

  useEffect(() => {
    dispatch(fetchFilesTemplates(companyId));

    return () => {
      dispatch(setDestroy());
    };
  }, [companyId, dispatch]);

  useEffect(() => {
    // After the company changes, the answer for the previous company must not
    // overwrite the names or mark the lookup as failed.
    let superseded = false;
    setLookupStatus("loading");
    Promise.all([fetchAllFileCategories(companyId), fetchFilesTypes(companyId)])
      .then(([categories, documentTypes]: [Category[], DocumentType[]]) => {
        if (superseded) return;
        setCategoryTitles(
          Object.fromEntries(categories.map((c) => [c.id, c.title]))
        );
        setDocumentTypeLabels(
          Object.fromEntries(documentTypes.map((d) => [d.id, d.label]))
        );
        setLookupStatus("loaded");
      })
      .catch((error) => {
        if (superseded) return;
        console.error(error);
        setLookupStatus("failed");
      });
    return () => {
      superseded = true;
    };
  }, [companyId]);

  // Dokobit and SignOnTab are product names, so they are not translated.
  const signingLabel = (signType?: string | null) => {
    switch (signType) {
      case "Click":
        return t("settings_templates_sign_click");
      case "Dokobit":
        return "Dokobit";
      case "Signontab":
        return "SignOnTab";
      case "Manual":
        return t("settings_templates_sign_manual");
      default:
        return DASH;
    }
  };

  const templatesPath = `/app/${companyId}/files/templates`;

  const renderBody = () => {
    if (isError || lookupStatus === "failed") {
      return (
        <StatusBanner
          tone="error"
          title={t("settings_templates_load_failed")}
        />
      );
    }
    if (isLoading || lookupStatus === "loading") {
      return <p className="text-sm text-gray-500">{t("loading")}</p>;
    }
    return (
      <CatalogTable
        columns={[
          { key: "name", header: t("settings_templates_col_name") },
          { key: "category", header: t("settings_templates_col_category") },
          { key: "documentType", header: t("settings_templates_col_type") },
          { key: "signing", header: t("settings_templates_col_signing") },
        ]}
        rows={(collection as Template[]).map((template) => ({
          id: template.id,
          cells: {
            name: <span className="font-medium">{template.name}</span>,
            category: nameOf(categoryTitles, template.category),
            documentType: nameOf(documentTypeLabels, template.templateType),
            signing: signingLabel(template.signType),
          },
        }))}
        renderRowAction={(id) => (
          <ChangeLink
            label={t("open")}
            onClick={() => navigate(`${templatesPath}/${id}`)}
          />
        )}
        empty={t("settings_templates_empty")}
      />
    );
  };

  return (
    <SettingsCard
      title={t("settings_templates_title")}
      description={t("settings_templates_description")}
      actions={
        <Link to={`${templatesPath}/new`} className={btnPrimaryCls}>
          {t("settings_templates_new")}
        </Link>
      }
    >
      {renderBody()}
    </SettingsCard>
  );
}

export default FilesTemplatesContainer;
